import { LiikkuvaWifiSalesProductOnlineModel } from '../../../common/constants/subscriptionConstants.js';
import { ModelType, VirtualModelType } from '../../../common/enums.js';
import { fetchOnlineModels, fetchV3PrivateOnlineModelHeaders, fetchV3PrivateOnlineModels } from '../../../common/fetch';
import { filterPricesByAccountSettings } from '../../../common/utils/priceUtils';
import { isEmployeeUser } from '../../../components/ProductDetails/utils/productDetailsEmployeeUtils';
import { SalesType } from '../../../generated/api/salesType.js';
import { OnlineModelQueryActionType } from './onlineModelReducer';
import type { OnlineModelQueryAction } from './onlineModelReducer';
import type { CompanyInfoResponse } from '../../../generated/api/companyInfoResponse';
import type { OnlineModel } from '../../../generated/api/onlineModel';
import type { OnlineModelHeader } from '../../../generated/api/onlineModelHeader';
import type { AuthenticatedUser } from '../authState';
const BATCH_SIZE = 50;

// ─── Models: direct parallel fetch (no queue) ────────────────────────────────

/**
 * Fetch private (priced) models in parallel. Each code is a separate API call.
 * Suitable for product details (1-2), cart (1-10), checkout (1-10).
 */
export const fetchPrivateOnlineModels = async (
  codes: string[],
  dispatch: React.Dispatch<OnlineModelQueryAction>,
  mdmId?: string,
  voucherCode?: string
) => {
  const models = await fetchV3PrivateOnlineModels(
    codes,
    voucherCode,
    voucherCode ? SalesType.NEW_SALE : undefined,
    false,
    mdmId
  );
  dispatch({
    type: OnlineModelQueryActionType.GET_PRIVATE_ONLINE_MODELS_SUCCESS,
    models,
    voucherCode,
  });
};

/**
 * Fetch public (non-priced) models in parallel.
 */
export const queryPublicOnlineModels = async (codes: string[], dispatch: React.Dispatch<OnlineModelQueryAction>) => {
  const models = await fetchOnlineModels(codes);
  dispatch({
    type: OnlineModelQueryActionType.GET_PUBLIC_ONLINE_MODELS_SUCCESS,
    models,
  });
};

// ─── Headers: batched requests (50 per request) ───────────────────────────────

/**
 * Fetch private headers in batches of 50. Used by category list pages.
 * The headers endpoint accepts multiple codes in one call.
 * Always fetches base customer prices (no voucher) — voucher pricing is handled by loadVoucherModels.
 */
export const batchQueryPrivateOnlineModelHeaders = async (
  codes: string[],
  dispatch: React.Dispatch<OnlineModelQueryAction>,
  mdmId?: string
) => {
  const batches: string[][] = [];
  for (let i = 0; i < codes.length; i += BATCH_SIZE) {
    batches.push(codes.slice(i, i + BATCH_SIZE));
  }
  // Batch failures intentionally propagate (no per-batch catch): the caller (fetchHeadersByCodes in
  // onlineModelContextProvider.tsx) removes the requested codes on rejection, which is what allows a
  // failed batch to be retried on the next render/call instead of being silently treated as "resolved, no data".
  return Promise.all(
    batches.map(async batch => {
      const headers = await fetchV3PrivateOnlineModelHeaders(batch, mdmId);
      dispatch({
        type: OnlineModelQueryActionType.GET_PRIVATE_ONLINE_MODEL_HEADERS_SUCCESS,
        headers,
        onlineModelCodes: batch,
      });
      return { onlineModelCodes: batch, headers };
    })
  );
};

// ─── Voucher: fetches both models + headers for voucher pricing ──────────────

export const queryVoucherModels = async (
  voucherCode: string,
  onlineModelCodeGuids: string[],
  dispatch: React.Dispatch<OnlineModelQueryAction>,
  mdmId?: string
) => {
  const models = await fetchV3PrivateOnlineModels(onlineModelCodeGuids, voucherCode, SalesType.NEW_SALE, true, mdmId);
  dispatch({
    type: OnlineModelQueryActionType.GET_PRIVATE_ONLINE_MODELS_SUCCESS,
    models,
    voucherCode,
  });
  return { models };
};

// ─── Utilities (unchanged) ───────────────────────────────────────────────────

const resolveHeaderForAccount = (
  onlineModelHeader: OnlineModelHeader,
  companyInfo?: CompanyInfoResponse,
  authenticatedUser?: AuthenticatedUser | null,
  onlineModelHeadersByCode?: Record<string, OnlineModelHeader>
) => {
  const privateHeader =
    onlineModelHeadersByCode && onlineModelHeader.onlineModelCode in onlineModelHeadersByCode
      ? onlineModelHeadersByCode[onlineModelHeader.onlineModelCode]
      : undefined;

  if (privateHeader) {
    return privateHeader;
  }

  return filterPricesByAccountSettings([onlineModelHeader], companyInfo, authenticatedUser)[0];
};

export const getFilteredHeaders = (
  headers: OnlineModelHeader[],
  companyInfo?: CompanyInfoResponse,
  authenticatedUser?: AuthenticatedUser | null,
  onlineModelHeadersByCode?: Record<string, OnlineModelHeader>
) => {
  return headers
    .filter(onlineModelHeader => !onlineModelHeader.tags?.includes('Hide from list'))
    .map(onlineModelHeader =>
      resolveHeaderForAccount(onlineModelHeader, companyInfo, authenticatedUser, onlineModelHeadersByCode)
    );
};

/**
 * Expands model codes to include related models that should be loaded together.
 * Handles MobileBroadband ↔ ElisaNetti bidirectional expansion.
 * Also includes MBB + ElisaNetti when SalesProduct WiFi codes are present,
 * so that SELF_SERVICE addon pricing (with campaign discounts) is available in basket.
 */
const salesProductCodes = Object.values(LiikkuvaWifiSalesProductOnlineModel) as string[];

export const expandRelatedModelCodes = (modelCodes: string[]): string[] => {
  const hasLiikkuvaWifi = modelCodes.includes(VirtualModelType.LiikkuvaWifi);
  if (hasLiikkuvaWifi) {
    const realCodes = modelCodes.filter(code => code !== VirtualModelType.LiikkuvaWifi);
    return expandRelatedModelCodes([
      ...realCodes,
      ...salesProductCodes,
      ModelType.MobileBroadband,
      ModelType.ElisaNetti,
    ]);
  }

  const hasElisaNetti = modelCodes.includes(ModelType.ElisaNetti);
  const hasMobileBroadband = modelCodes.includes(ModelType.MobileBroadband);
  const hasSalesProductWifi = modelCodes.some(code => salesProductCodes.includes(code));

  const expanded = [...modelCodes];

  if ((hasElisaNetti || hasSalesProductWifi) && !hasMobileBroadband) {
    expanded.push(ModelType.MobileBroadband);
  }
  if ((hasMobileBroadband || hasSalesProductWifi) && !hasElisaNetti) {
    expanded.push(ModelType.ElisaNetti);
  }
  return expanded;
};

/**
 * Merges Liikkuva WiFi offers into MobileBroadband model for consistent basket handling.
 */
export const mergeMobileBroadbandModels = (models: OnlineModel[]): OnlineModel[] => {
  const mobileBroadbandModel = models.find(m => m.onlineModelCode === ModelType.MobileBroadband);
  const elisaNettiModel = models.find(m => m.onlineModelCode === ModelType.ElisaNetti);

  if (mobileBroadbandModel && elisaNettiModel) {
    const mergedModel: OnlineModel = {
      ...mobileBroadbandModel,
      offers: [...(mobileBroadbandModel.offers ?? []), ...(elisaNettiModel.offers ?? [])],
      addOns: [...(mobileBroadbandModel.addOns ?? []), ...(elisaNettiModel.addOns ?? [])],
    };
    return models.map(m => (m.onlineModelCode === ModelType.MobileBroadband ? mergedModel : m));
  }
  return models;
};

export const filterByOnlineModelCodes = (
  onlineModelsByCode: Record<string, OnlineModel> | undefined,
  listOfOnlineModelCodes: string[]
) =>
  (onlineModelsByCode
    ? Object.values(onlineModelsByCode).filter(model => listOfOnlineModelCodes.includes(model.onlineModelCode))
    : undefined) || [];

export const discountsLoaded = (
  user: AuthenticatedUser | undefined | null,
  models: OnlineModelHeader[] | OnlineModel[],
  accountSpecificModels?: Record<string, OnlineModelHeader> | Record<string, OnlineModel>
) => {
  const privateModels = accountSpecificModels ?? {};
  if (user && !isEmployeeUser(user)) {
    return !(
      Object.keys(privateModels).length === 0 || models.some(model => !(model.onlineModelCode in privateModels))
    );
  }
  return true;
};

export const getOnlineModelsWithVoucher = (
  onlineModelCodeGuids: string[],
  activeVoucherCode: string,
  onlineModelsByVoucher?: Record<string, Record<string, OnlineModel>>
) => {
  return filterByOnlineModelCodes((onlineModelsByVoucher ?? {})[activeVoucherCode] ?? {}, onlineModelCodeGuids);
};

export const areAllModelCodesLoaded = (modelCodes: string[], models: OnlineModel[]): boolean => {
  if (modelCodes.length === 0) {
    return true;
  }
  const loadedCodes = new Set(models.map(model => model.onlineModelCode));
  return modelCodes.every(code => loadedCodes.has(code));
};
